import * as React from "react";

type HoverHandler = (e: React.MouseEvent | React.FocusEvent) => void;

export type A11yHoverOptions = {
  /** Start/end of the hoverable affordance (also called on focus/blur) */
  onHoverStart?: HoverHandler; // e.g., show tooltip, open popover
  onHoverEnd?: HoverHandler; // e.g., hide tooltip, close popover

  /** Choose which mouse pair to use (enter/leave recommended) */
  mousePair?: "enter-leave" | "over-out"; // default 'enter-leave'

  /** Disable all interactions (handlers guarded) */
  disabled?: boolean;

  /**
   * Make the element keyboard-focusable (adds tabIndex if true).
   * Does NOT add role or keyboard activation (kept separate from "button" behavior).
   */
  focusable?: boolean;
  /** Override tabIndex explicitly if needed. Ignored unless provided. */
  tabIndex?: number;

  /** ARIA compatibility for popup/disclosure or tooltip */
  ariaHaspopup?: React.HTMLAttributes<HTMLElement>["aria-haspopup"];
  ariaExpanded?: boolean;
  ariaControls?: string; // id of the controlled popup
  ariaDescribedby?: string; // id of the tooltip or description element
};

/**
 * Returns props to spread on any element so that mouse hover is paired with keyboard focus,
 * and unhover is paired with blur. Adds no role or keyboard activation.
 */
export function getA11yHoverableProps({
  onHoverStart,
  onHoverEnd,
  mousePair = "enter-leave",
  disabled = false,
  focusable = false,
  tabIndex,
  ariaHaspopup,
  ariaExpanded,
  ariaControls,
  ariaDescribedby,
}: A11yHoverOptions = {}) {
  const hoverStart: HoverHandler = (e) => {
    if (disabled) return;
    onHoverStart?.(e);
  };

  const hoverEnd: HoverHandler = (e) => {
    if (disabled) return;
    onHoverEnd?.(e);
  };

  const mouseHandlers =
    mousePair === "enter-leave"
      ? { onMouseEnter: hoverStart, onMouseLeave: hoverEnd }
      : { onMouseOver: hoverStart, onMouseOut: hoverEnd };

  const a11yProps: React.HTMLAttributes<HTMLElement> = {
    onFocus: hoverStart,
    onBlur: hoverEnd,
    ...mouseHandlers,
  };

  // Optional focusability
  if (typeof tabIndex === "number") {
    a11yProps.tabIndex = tabIndex;
  } else if (focusable) {
    a11yProps.tabIndex = 0;
  }

  // ARIA attributes
  if (ariaHaspopup !== undefined) a11yProps["aria-haspopup"] = ariaHaspopup;
  if (ariaExpanded !== undefined) a11yProps["aria-expanded"] = ariaExpanded;
  if (ariaControls) a11yProps["aria-controls"] = ariaControls;
  if (ariaDescribedby) a11yProps["aria-describedby"] = ariaDescribedby;

  if (disabled) a11yProps["aria-disabled"] = true;

  return a11yProps;
}
